/**
 * Hook pour la logique de jeu côté client
 */

import { useCallback } from 'react';
import { socketService } from '../services/socketService';
import { useGameStore } from '../store/gameStore';
import type { Position, Wall, WallOrientation } from '@pathblock/core';

// Convertit les coordonnées réelles (depuis la vue inversée du joueur 1)
// en coordonnées serveur correctes selon l'orientation du mur.
// Le joueur 1 voit le plateau retourné à 180°, donc les murs doivent être ajustés :
// - horizontal { row, col } → { row: row+1, col: col-1 }
// - vertical   { row, col } → { row: row-1, col: col+1 }
function adjustWallForFlip(row: number, col: number, orientation: WallOrientation): { row: number; col: number } {
  if (orientation === 'horizontal') {
    return { row: row + 1, col: col - 1 };
  } else {
    return { row: row - 1, col: col + 1 };
  }
}

export function useGame() {
  const {
    gameState,
    playerId,
    placingWall,
    wallOrientation,
    wallPreview,
    setWallPreview,
    setPlacingWall,
    toggleWallOrientation,
  } = useGameStore();

  const isMyTurn = useGameStore((state) => state.isMyTurn());
  const myPlayer = useGameStore((state) => state.getMyPlayer());

  const handleCellClick = useCallback(
    (row: number, col: number) => {
      if (!isMyTurn || !gameState) return;

      if (placingWall) {
        let wallCoords = { row, col };
        if (playerId === 1) {
          wallCoords = adjustWallForFlip(row, col, wallOrientation);
        }
        const wall: Wall = { ...wallCoords, orientation: wallOrientation };
        socketService.placeWall(wall);
      } else {
        const to: Position = { row, col };
        socketService.move(to);
      }
    },
    [isMyTurn, gameState, placingWall, wallOrientation, playerId]
  );

  const handleCellHover = useCallback(
    (row: number, col: number) => {
      if (!isMyTurn || !placingWall) {
        setWallPreview(null);
        return;
      }

      let wallCoords = { row, col };
      if (playerId === 1) {
        wallCoords = adjustWallForFlip(row, col, wallOrientation);
      }
      setWallPreview({ ...wallCoords, orientation: wallOrientation });
    },
    [isMyTurn, placingWall, wallOrientation, setWallPreview, playerId]
  );

  const togglePlacingWall = useCallback(() => {
    if (!myPlayer || myPlayer.wallsRemaining <= 0) return;
    setPlacingWall(!placingWall);
  }, [myPlayer, placingWall, setPlacingWall]);

  // Phase de placement : le joueur clique sur une case de sa ligne de départ
  const handlePlacementClick = useCallback(
    (col: number) => {
      if (!gameState || gameState.phase !== 'placement' || !playerId) return;
      const myPlayerState = gameState.players[playerId - 1];
      if (myPlayerState.positionConfirmed) return;
      socketService.choosePosition(col);
    },
    [gameState, playerId]
  );

  return {
    gameState,
    playerId,
    isMyTurn,
    myPlayer,
    placingWall,
    wallOrientation,
    wallPreview,
    handleCellClick,
    handleCellHover,
    handlePlacementClick,
    togglePlacingWall,
    toggleWallOrientation,
  };
}
